import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { SprayRecord } from '../data-models/spray-record.model';
import { SprayRecordStatus } from '../data-models/spray-record-status.model';
import { DroneAvailabilityQuery } from '../request-models/drone-availability-query.model';
import { SprayRecordRequest } from '../request-models/spray-record-request.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { DroneAvailability } from '../response-models/drone-availability.model';
import { Page } from '../response-models/page.model';
import { Paging } from '../response-models/paging.model';
import { SprayRecordListItem } from '../response-models/spray-record-list-item.model';
import { SprayRecordSummary } from '../response-models/spray-record-summary.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, idString, listOf, nullableBoolean, nullableNumber, nullableString, stringArrayOrEmpty } from '../utils/wire-value-utils';
import { toSprayRecordRequest } from '../utils/spray-record-request-utils';
import { OrganizationContextService } from '../services/organization-context.service';

/** Rows per page of the spray records list. */
const DEFAULT_PAGE_SIZE = 25;

/** Filters and page for `GET /spray-records`. The hub caps `limit` at 100. */
export interface SprayRecordListOptions {
  status?: string;
  fieldId?: string;
  pilotId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

/**
 * Spray records of the current organization: `/spray-records[/:id]` and
 * `/spray-records/drone-flights/availability` (hub `user-spray-records-controller.ts`). The hub scopes every
 * route to the organization the auth interceptor sends.
 *
 * The list endpoint returns summary rows (`SprayRecordSummary`), held as signal state; a single record
 * (`SprayRecord`) carries its chemicals and drone flights. CRUD failures set `error` (the hub's message) and
 * resolve to `null` / `false`.
 */
@Injectable({
  providedIn: 'root'
})
export class SprayRecordService extends BaseService<SprayRecord> {
  private organizationContext = inject(OrganizationContextService);

  // Signal-based state management
  private recordsSignal = signal<SprayRecordSummary[]>([]);
  private pagingSignal = signal<Paging | null>(null);
  /** The filters and page of the loaded list; `loadRecords()` without options reloads it. */
  private listOptions: SprayRecordListOptions = {};
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  /** The loaded page of records. */
  readonly records = computed(() => this.recordsSignal());
  /** Paging of the loaded page (`totalItems` counts every record matching the filters). */
  readonly paging = computed(() => this.pagingSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Computed helpers over the loaded page
  readonly completedRecords = computed(() =>
    this.recordsSignal().filter(r => r.status === 'completed')
  );

  readonly pendingRecords = computed(() =>
    this.recordsSignal().filter(r => r.status === 'pending')
  );

  readonly totalAcreageCompleted = computed(() =>
    this.completedRecords().reduce((sum, r) => sum + r.totalAcreage, 0)
  );

  constructor() {
    super('spray-records', SprayRecord);
    // React to organization context changes — load records when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadRecords({});
      }
    });
  }

  /**
   * Load one page of spray records into `records` and `paging`. Without `options` it reloads the current filters
   * and page (e.g. after a save or a realtime event). Keeps the current list on failure.
   */
  async loadRecords(options?: SprayRecordListOptions): Promise<void> {
    if (options) {
      this.listOptions = options;
    }
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    const page = await this.track('Failed to load spray records', () => this.fetchPage(this.listOptions));
    if (page) {
      this.recordsSignal.set(page.items);
      this.pagingSignal.set(page.paging);
    }
  }

  /** One page of spray records, without touching the loaded list. Rejects with an `ApiError`. */
  async fetchPage(options: SprayRecordListOptions = {}): Promise<Page<SprayRecordSummary>> {
    const query = {
      status: options.status || undefined,
      fieldId: options.fieldId || undefined,
      pilotId: options.pilotId || undefined,
      startDate: options.startDate || undefined,
      endDate: options.endDate || undefined,
      page: options.page || 1,
      limit: options.limit || DEFAULT_PAGE_SIZE,
    };
    // The list rows are summaries, not records, so they are read here rather than through `getPage`.
    return firstValueFrom(this.requestEnvelope('GET', { query }, toSummaryPage));
  }

  /** How many records have `status` (the hub's `paging.totalItems` for a one-row page). Rejects with an `ApiError`. */
  async countRecords(status: SprayRecordStatus): Promise<number> {
    const page = await this.fetchPage({ status, limit: 1 });
    return page.paging.totalItems;
  }

  /**
   * Get all records (from signal)
   */
  getRecords(): SprayRecordSummary[] {
    return this.recordsSignal();
  }

  /**
   * Get records as flattened list items for display
   */
  getRecordsAsListItems(): SprayRecordListItem[] {
    return this.recordsSignal().map(toListItem);
  }

  /**
   * Get a single record (with chemicals and drone flights) from the API
   */
  async getRecordById(id: string): Promise<SprayRecord | null> {
    return this.track('Failed to fetch spray record', () => firstValueFrom(this.get(id)));
  }

  /**
   * Is a drone free for a slot, or is it already flying for someone else?
   *
   * Advisory only — a failed check resolves to "available" so a flaky network
   * can never stop a user saving.
   */
  async checkDroneAvailability(params: DroneAvailabilityQuery): Promise<DroneAvailability> {
    try {
      const query = { ...params, excludeSprayRecordId: params.excludeSprayRecordId || undefined };
      const opts = { path: 'drone-flights/availability', query };
      return await firstValueFrom(this.request('GET', opts, toDroneAvailability));
    } catch (error: unknown) {
      console.error('[SprayRecordService] Error checking drone availability:', ApiError.fromHttpError(error).message);
      return { available: true };
    }
  }

  /**
   * Create a new spray record (with its chemicals and drone flights)
   */
  async createRecord(dto: SprayRecordRequest): Promise<SprayRecord | null> {
    return this.saveAndReload('Failed to create spray record', () =>
      firstValueFrom(this.create(toSprayRecordRequest(dto)))
    );
  }

  /**
   * Update an existing spray record (the hub records a revision). Send `null` for an optional field the user
   * emptied.
   */
  async updateRecord(id: string, dto: SprayRecordRequest): Promise<SprayRecord | null> {
    return this.saveAndReload('Failed to update spray record', () =>
      firstValueFrom(this.update(id, toSprayRecordRequest(dto)))
    );
  }

  /**
   * Delete a spray record
   */
  async deleteRecord(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete spray record', async () => {
      await firstValueFrom(this.delete(id));
      return true;
    });
    if (deleted) {
      // Reload rather than drop the row, so the page refills and the total stays right.
      await this.loadRecords();
    }
    return deleted ?? false;
  }

  /**
   * Get unique field names for filter dropdowns
   */
  getUniqueFields(): string[] {
    const fields = this.recordsSignal()
      .map(r => r.fieldName)
      .filter((name): name is string => !!name);
    return [...new Set(fields)];
  }

  /**
   * Get unique pilot names for filter dropdowns
   */
  getUniquePilots(): string[] {
    const pilots = this.recordsSignal()
      .map(r => r.pilotName)
      .filter((name): name is string => !!name);
    return [...new Set(pilots)];
  }

  /**
   * Filter records by various criteria (client-side filtering)
   */
  filterRecords(filters: {
    status?: string;
    field?: string;
    pilot?: string;
    dateFrom?: string;
    dateTo?: string;
  }): SprayRecordSummary[] {
    const field = filters.field?.toLowerCase();
    const pilot = filters.pilot?.toLowerCase();
    return this.recordsSignal().filter(r =>
      (!filters.status || r.status === filters.status) &&
      (!field || !!r.fieldName?.toLowerCase().includes(field)) &&
      (!pilot || !!r.pilotName?.toLowerCase().includes(pilot)) &&
      (!filters.dateFrom || (r.applicationDate ?? '') >= filters.dateFrom) &&
      (!filters.dateTo || (r.applicationDate ?? '') <= filters.dateTo)
    );
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Saves, then reloads the list: the save response is a full record, not a list row. */
  private async saveAndReload(fallback: string, save: () => Promise<SprayRecord | null>): Promise<SprayRecord | null> {
    const saved = await this.track(fallback, save);
    if (saved) {
      await this.loadRecords();
    }
    return saved;
  }

  /**
   * Runs an action with the loading flag set. A failure records the hub's message (or `fallback`) in `error`
   * and resolves to `null`.
   */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? fallback;
      console.error(`[SprayRecordService] ${fallback}:`, message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }
}

function toSummaryPage(body: ApiListResponse<unknown> | null): Page<SprayRecordSummary> {
  const items = listOf(body?.data, toSprayRecordSummary);
  const paging = body?.paging ?? { page: 1, pageSize: items.length, totalItems: items.length, totalPages: 1 };
  return { items, paging };
}

function toSprayRecordSummary(data: unknown): SprayRecordSummary {
  const source = isRecord(data) ? data : {};
  return {
    id: idString(source['id']),
    organizationId: nullableString(source['organizationId']) ?? null,
    fieldId: nullableString(source['fieldId']) ?? null,
    workOrderId: nullableString(source['workOrderId']) ?? null,
    applicationDate: nullableString(source['applicationDate']) ?? null,
    status: (nullableString(source['status']) ?? null) as SprayRecordStatus | null,
    certificationId: nullableString(source['certificationId']) ?? null,
    notes: nullableString(source['notes']) ?? null,
    weatherConditions: nullableString(source['weatherConditions']) ?? null,
    fieldName: nullableString(source['fieldName']) ?? null,
    cropType: nullableString(source['cropType']) ?? null,
    customerId: nullableString(source['customerId']) ?? null,
    customerName: nullableString(source['customerName']) ?? null,
    totalAcreage: countOrZero(source['totalAcreage']),
    droneCount: countOrZero(source['droneCount']),
    droneNames: stringArrayOrEmpty(source['droneNames']),
    pilotName: nullableString(source['pilotName']) ?? null,
    // `?? null`, not `|| null` — a genuine 0 would be lost by the latter.
    filedFlights: nullableNumber(source['filedFlights']) ?? null,
    needsFlightCount: nullableBoolean(source['needsFlightCount']) === true,
    chemicals: listOf(source['chemicals'], toChemicalSummary),
  };
}

function toChemicalSummary(data: unknown): { id: string; name: string; rate: string | null } {
  const source = isRecord(data) ? data : {};
  return { id: idString(source['id']), name: nullableString(source['name']) ?? '', rate: nullableString(source['rate']) ?? null };
}

/** A summary row flattened for the list view, with display fallbacks. */
function toListItem(record: SprayRecordSummary): SprayRecordListItem {
  return {
    id: record.id,
    date: record.applicationDate ?? '',
    fieldName: record.fieldName || 'Unknown Field',
    customerName: record.customerName ?? undefined,
    customerId: record.customerId ?? undefined,
    cropType: record.cropType || 'Unknown',
    acreage: record.totalAcreage,
    filedFlights: record.filedFlights,
    needsFlightCount: record.needsFlightCount,
    chemicalName: record.chemicals[0]?.name || 'N/A',
    applicationRate: record.chemicals[0]?.rate || 'N/A',
    pilotName: record.pilotName || 'Unknown',
    droneId: record.droneNames[0] || 'N/A',
    status: record.status ?? 'pending',
    weatherConditions: record.weatherConditions ?? undefined,
    notes: record.notes ?? undefined
  };
}

function toDroneAvailability(data: unknown): DroneAvailability {
  const source = isRecord(data) ? data : {};
  const message = nullableString(source['message']);
  return { available: source['available'] !== false, ...(message ? { message } : {}) };
}
